import NumberFlow from "@number-flow/react";
import { useSuspenseQuery } from "@tanstack/react-query";
import {
  getControlCategory,
  isDataControl,
  queries,
  useAuthConfig,
} from "@theprelude/core";
import {
  Button,
  ChevronIcon,
  ControlIcon,
  PlatformIcon,
  ResourceTypeIcon,
  SettingsErrorIcon,
  ShieldFile,
  Truncate,
} from "@theprelude/ds";
import { ValidControlCategory } from "@theprelude/sdk";
import { useOverlay } from "@/hooks/use-overlay";
import { tb } from "@/lib/tracking";
import { criticalSettingsQuery } from "@/queries/policies";
import { Card, CardHeader, ListItem } from "../sections";
import { SummaryIconBubble, SummaryStatusIcon } from "../summary-icon-bubbles";

interface Props {
  category: ValidControlCategory;
}

export const CategoryCriticalMisconfigurationsCard = ({ category }: Props) => {
  const overlay = useOverlay();

  return (
    <Card className="flex flex-col gap-4">
      <CardHeader>
        <SummaryStatusIcon success={false} />
        Critical Misconfigurations
      </CardHeader>
      <PoliciesList category={category} />
      <Button
        intent="secondary"
        icon={<ShieldFile className="text-secondary-foreground" />}
        className={tb("review-critical-misconfigs")}
        onClick={() => overlay.open("critical-policies", { category })}
      >
        Review critical misconfigurations
      </Button>
    </Card>
  );
};

const PoliciesList = ({ category }: Props) => {
  const overlay = useOverlay();
  const authConfig = useAuthConfig();

  const { data: account } = useSuspenseQuery(
    queries.iam.getAccount(authConfig),
  );
  const controls = account.controls.filter(
    (control) =>
      getControlCategory(control.id) === category && !isDataControl(control.id),
  );

  const { data: settings } = criticalSettingsQuery(
    authConfig,
    controls,
  ).useSuspenseQuery();

  return (
    <div>
      {settings.slice(0, 5).map((setting) => (
        <ListItem
          key={`${setting.instanceId}-${setting.policyId}-${setting.name}`}
          className="flex flex-col gap-1"
        >
          <div className="flex items-center gap-2">
            <SummaryIconBubble status="negative" size="small">
              <SettingsErrorIcon />
            </SummaryIconBubble>
            <div className="min-w-0 text-status-red font-medium">
              <Truncate text={setting.name}>{setting.name}</Truncate>
            </div>
          </div>
          <div className="grid w-full grid-cols-[5fr,1fr,1fr] gap-2">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 pl-1">
                <ControlIcon
                  control={setting.control}
                  className="text-secondary-foreground"
                />
                {setting.platform && (
                  <ChevronIcon className="rotate-90 text-secondary-foreground" />
                )}
                {setting.platform && (
                  <PlatformIcon
                    platform={setting.platform}
                    className="text-secondary-foreground"
                  />
                )}
              </div>
              <div className="min-w-0">
                <Truncate text={setting.policyName}>
                  {setting.policyName}
                </Truncate>
              </div>
            </div>
            <div className="flex items-center gap-1 font-medium">
              <ResourceTypeIcon
                resourceType={setting.resourceType}
                className="text-secondary-foreground"
              />
              <NumberFlow value={setting.resourceCount} />
            </div>
            <Button
              intent="pill"
              onClick={() =>
                overlay.open("policies", {
                  filter: "critical",
                  control: setting.control,
                  instanceId: setting.instanceId,
                })
              }
            >
              View Policy
            </Button>
          </div>
        </ListItem>
      ))}
    </div>
  );
};
